import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert, Box, Button, Card, CardActionArea, CardContent, Chip, FormControl,
  InputLabel, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material'
import { Add as AddIcon, Business as BusinessIcon } from '@mui/icons-material'
import { useCompanies } from '../../hooks/api/useCompanies'
import LoadingSpinner from '../../components/ui/LoadingSpinner'

const featureLabels = [
  ['sales_tax_invoice', 'STI'],
  ['advance_tax', 'Advance tax'],
  ['carton_discount_pricing', 'Carton pricing'],
] as const

const CompanyListPage = () => {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const { data: companies = [], isLoading, error } = useCompanies({
    search: search.trim() || undefined,
    status: status || undefined,
  })

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Companies</Typography>
          <Typography color="text.secondary">Manage tenant access, features, and billing settings.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/companies/new')}>
          Create company
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
        <TextField label="Search companies" value={search} onChange={(event) => setSearch(event.target.value)} sx={{ minWidth: 280, flexGrow: 1 }} size="small" />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select value={status} label="Status" onChange={(event) => setStatus(event.target.value)}>
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="suspended">Suspended</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{(error as Error).message || 'Could not load companies.'}</Alert>}
      {isLoading && <LoadingSpinner message="Loading companies..." />}
      {!isLoading && !error && companies.length === 0 && (
        <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: 6 }}>
          <BusinessIcon color="disabled" sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h6">No companies found</Typography>
          <Typography color="text.secondary">Try another search or create a company.</Typography>
        </CardContent></Card>
      )}
      {!isLoading && !error && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
          {companies.map((company) => (
            <Card key={company._id} variant="outlined" sx={{ height: '100%' }}>
              <CardActionArea onClick={() => navigate(`/companies/${company._id}`)} sx={{ height: '100%', alignItems: 'stretch' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} mb={1}>
                    <Typography variant="h6" fontWeight={700}>{company.name}</Typography>
                    <Chip size="small" label={company.status === 'active' ? 'active' : 'suspended'} color={company.status === 'active' ? 'success' : 'warning'} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {company.unit_of_measure.code} · {company.billing_config.discount_type === 'whole_bill' ? 'Whole bill discount' : 'Per SKU discount'}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.75}>
                    {featureLabels.map(([key, label]) => (
                      <Chip key={key} label={`${label}: ${company.features[key] ? 'On' : 'Off'}`} size="small" variant={company.features[key] ? 'filled' : 'outlined'} color={company.features[key] ? 'primary' : 'default'} />
                    ))}
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default CompanyListPage
